/*
 * gifted. — grouped colour picker
 * PATH: assets/gifted-colour-picker.js
 *
 * Companion to blocks/gifted-colour-picker.liquid
 *
 * This file never reimplements variant logic. It finds the theme's own control for the colour
 * option, visually hides it, and drives it by setting its value and dispatching real input and
 * change events. Horizon then updates price, gallery, availability, URL and add-to-cart exactly
 * as it normally would. If the theme's control cannot be found, this file does nothing and the
 * theme's picker stays visible — the page is never left without a working colour selector.
 *
 * No dependencies. No framework. Loaded with defer.
 */
(function () {
  'use strict';

  var HIDE = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap'
  };

  function fire(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  class GiftedColourPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.swatches = Array.prototype.slice.call(this.querySelectorAll('.gcp__sw'));
      this.currentEl = this.querySelector('[data-gcp-current]');
      if (!this.swatches.length) return;

      this.native = this.findNative();
      if (!this.native) return;

      this.hideNative();
      this.hidden = false;

      var self = this;
      this.swatches.forEach(function (sw) {
        sw.addEventListener('click', function () {
          self.select(sw.dataset.value);
        });
        sw.addEventListener('keydown', function (e) {
          self.onKeydown(e, sw);
        });
      });

      this.onNativeChange = function () {
        self.syncFromNative();
      };
      this.nativeRoot().addEventListener('change', this.onNativeChange);

      this.syncFromNative(true);
    }

    disconnectedCallback() {
      if (this.onNativeChange && this.native) {
        this.nativeRoot().removeEventListener('change', this.onNativeChange);
      }
    }

    scope() {
      return this.closest('form[action*="/cart/add"]') || this.closest('.shopify-section') || document;
    }

    findNative() {
      var scope = this.scope();
      var values = {};
      this.swatches.forEach(function (s) {
        values[s.dataset.value] = true;
      });

      if (this.dataset.nativeSelector) {
        var pinned = scope.querySelector(this.dataset.nativeSelector);
        var wrapped = pinned ? this.wrap(pinned) : null;
        if (wrapped) return wrapped;
      }

      var containers = Array.prototype.slice.call(
        scope.querySelectorAll('fieldset, .variant-option, .product-form__input')
      );

      for (var i = 0; i < containers.length; i++) {
        var c = containers[i];
        var radios = Array.prototype.slice.call(c.querySelectorAll('input[type="radio"]'));
        var hit = radios.filter(function (r) {
          return values[r.value];
        });
        if (hit.length) return { kind: 'radio', inputs: radios, root: c };

        var sel = c.querySelector('select');
        if (sel) {
          var opts = Array.prototype.slice.call(sel.options).filter(function (o) {
            return values[o.value];
          });
          if (opts.length) return { kind: 'select', select: sel, root: c };
        }
      }

      var anyRadios = Array.prototype.slice
        .call(scope.querySelectorAll('input[type="radio"]'))
        .filter(function (r) {
          return values[r.value];
        });
      if (anyRadios.length) {
        return { kind: 'radio', inputs: anyRadios, root: anyRadios[0].form || scope };
      }

      var anySelect = Array.prototype.slice.call(scope.querySelectorAll('select')).find(function (s) {
        return Array.prototype.slice.call(s.options).some(function (o) {
          return values[o.value];
        });
      });
      if (anySelect) return { kind: 'select', select: anySelect, root: anySelect.form || scope };

      return null;
    }

    wrap(el) {
      if (el.tagName === 'SELECT') return { kind: 'select', select: el, root: el };
      var inputs = Array.prototype.slice.call(el.querySelectorAll('input[type="radio"]'));
      return inputs.length ? { kind: 'radio', inputs: inputs, root: el } : null;
    }

    nativeRoot() {
      return (this.native && this.native.root) || document;
    }

    hideNative() {
      var host =
        this.native.kind === 'select'
          ? this.native.select.closest('fieldset, .variant-option, .product-form__input') || this.native.select
          : this.native.root;
      if (!host || host === document) return;
      host.setAttribute('data-gcp-hidden', '');
      Object.keys(HIDE).forEach(function (k) {
        host.style[k] = HIDE[k];
      });
    }

    select(value) {
      if (this.native.kind === 'radio') {
        var input = this.native.inputs.find(function (i) {
          return i.value === value;
        });
        if (!input) return;
        if (!input.checked) {
          input.checked = true;
          fire(input);
        }
      } else {
        var sel = this.native.select;
        if (sel.value !== value) {
          sel.value = value;
          fire(sel);
        }
      }
      this.paint(value);
    }

    syncFromNative(initial) {
      var value = null;
      if (this.native.kind === 'radio') {
        var on = this.native.inputs.find(function (i) {
          return i.checked;
        });
        value = on ? on.value : null;
      } else {
        value = this.native.select.value;
      }
      this.paint(value, initial);
    }

    paint(value, initial) {
      var active = null;
      this.swatches.forEach(function (sw) {
        var on = sw.dataset.value === value;
        sw.setAttribute('aria-checked', on ? 'true' : 'false');
        sw.tabIndex = on ? 0 : -1;
        if (on) active = sw;
      });
      if (!active) this.swatches[0].tabIndex = 0;
      if (this.currentEl) this.currentEl.textContent = active ? active.dataset.label : '';
      if (active && !initial) active.scrollIntoView({ block: 'nearest' });
    }

    onKeydown(e, sw) {
      var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (keys.indexOf(e.key) === -1) return;
      e.preventDefault();
      var i = this.swatches.indexOf(sw);
      var last = this.swatches.length - 1;
      var n = i;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = i === last ? 0 : i + 1;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = i === 0 ? last : i - 1;
      if (e.key === 'Home') n = 0;
      if (e.key === 'End') n = last;
      var next = this.swatches[n];
      next.focus();
      this.select(next.dataset.value);
    }
  }

  if (!window.customElements.get('gifted-colour-picker')) {
    window.customElements.define('gifted-colour-picker', GiftedColourPicker);
  }
})();
